import { Alert, Box, FormHelperText, FormLabel, type SxProps, type Theme } from "@mui/material";
import { useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { labelOf } from "../../pages/pages";
import { FlexcellSpinner } from "../theme/FlexcellMark";
import { useTokens } from "../theme/theme";

// ---
// Shared building blocks
//
// Every component takes `sx` typed as SxProps<Theme> and merges it with the array form so a caller
// always wins, expresses intent through semantic props rather than raw style props, and reads its
// values from the design tokens so it follows light and dark.
// ---

/** Merges internal styles with a caller's `sx` so the caller wins on conflict. */
function merge(internal: SxProps<Theme>, sx?: SxProps<Theme>): SxProps<Theme> {
  return [internal, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>;
}

// ---
// page structure
// ---

/**
 * The panel's own bar, and whatever the page offers to do.
 *
 * The title is dropped when it only repeats what the trail above already says, which is every
 * page whose heading is its own name in the sidebar: a word printed twice, one line apart, earns
 * neither the 48px it costs nor the rule under it. The actions move up into that space, and stay
 * where they were — hard against the right edge.
 *
 * A title that says something else still gets its bar: edge to edge, hence the negative inset
 * against the body's padding, and sticky, so it stays with whatever is being read.
 */
export function PageHeader({
  title,
  action,
}: {
  title: string;
  action?: ReactNode;
}): React.JSX.Element {
  const tokens = useTokens();
  const segment = useLocation().pathname.split("/").filter(Boolean).pop() ?? "";

  if (labelOf(segment) === title) {
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 3,
          pt: 5,
          ...(action === undefined ? { pt: 0 } : {}),
        }}
      >
        {/* Shown to no one, read by everything: the trail says where you are visually, but a
            page still owes a screen reader a heading, and the trail is not one. */}
        <Box
          component="h1"
          sx={{
            position: "absolute",
            width: "1px",
            height: "1px",
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
            m: 0,
          }}
        >
          {title}
        </Box>
        {action}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1,
        mx: -6,
        px: 6,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "8px 12px",
        minHeight: 48,
        py: 2,
        mb: 5,
        bgcolor: tokens.surface.card,
        borderBottom: `1px solid ${tokens.line.faint}`,
      }}
    >
      <Box
        component="h1"
        sx={{
          m: 0,
          fontSize: 17,
          fontWeight: tokens.weight.semibold,
          letterSpacing: "-0.01em",
          color: tokens.ink.primary,
        }}
      >
        {title}
      </Box>
      <Box sx={{ flex: 1 }} />
      {action}
    </Box>
  );
}

/**
 * A titled card.
 * `tone="warning"` rings it for something that needs acting on now, like a secret shown once.
 * `flush` drops the body's inset so a table runs edge to edge.
 */
export function Section({
  title,
  action,
  children,
  tone = "neutral",
  flush = false,
  sx,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  tone?: "neutral" | "warning";
  flush?: boolean;
  sx?: SxProps<Theme>;
}): React.JSX.Element {
  const tokens = useTokens();
  const headed = title !== undefined || action !== undefined;
  return (
    <Box
      component="section"
      sx={merge(
        {
          mt: 5,
          bgcolor: tokens.surface.card,
          border: `1px solid ${tone === "warning" ? tokens.semantic.warning : tokens.line.default}`,
          borderRadius: `${tokens.radius.md}px`,
          overflow: "hidden",
        },
        sx,
      )}
    >
      {headed && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            minHeight: 44,
            px: 5,
            pt: 3,
            pb: flush ? 3 : 0,
            ...(flush ? { borderBottom: `1px solid ${tokens.line.default}` } : {}),
          }}
        >
          {title !== undefined && (
            <Box component="h2" sx={{ typography: "overline", color: tokens.ink.muted, m: 0 }}>
              {title}
            </Box>
          )}
          {action}
        </Box>
      )}
      <Box
        sx={
          flush
            ? {
                "& .MuiTableCell-root:first-of-type": { pl: 5 },
                "& .MuiTableCell-root:last-of-type": { pr: 5 },
              }
            : { px: 5, pt: headed ? 3 : 5, pb: 5 }
        }
      >
        {children}
      </Box>
    </Box>
  );
}

/** A row of form controls that wraps, with buttons aligned to the inputs rather than the labels. */
export function FormRow({
  children,
  sx,
}: {
  children: ReactNode;
  sx?: SxProps<Theme>;
}): React.JSX.Element {
  return (
    <Box sx={merge({ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 3 }, sx)}>
      {children}
    </Box>
  );
}

/** A label above its control, and a hint or an error below it — the design's `.field`. */
export function Field({
  label,
  hint,
  error = false,
  children,
  sx,
}: {
  label: string;
  hint?: ReactNode;
  error?: boolean;
  children: ReactNode;
  sx?: SxProps<Theme>;
}): React.JSX.Element {
  return (
    <Box sx={merge({ display: "flex", flexDirection: "column", minWidth: 0 }, sx)}>
      <FormLabel error={error}>{label}</FormLabel>
      {children}
      {hint !== undefined && <FormHelperText error={error}>{hint}</FormHelperText>}
    </Box>
  );
}

// ---
// text
// ---

/** Secondary prose under a control or inside a card. */
export function Hint({
  children,
  sx,
}: {
  children: ReactNode;
  sx?: SxProps<Theme>;
}): React.JSX.Element {
  const tokens = useTokens();
  return (
    <Box
      component="p"
      sx={merge({ typography: "body2", color: tokens.ink.muted, m: 0, mt: 3 }, sx)}
    >
      {children}
    </Box>
  );
}

/** What a list shows when it has nothing in it, and what to do about that. */
export function Empty({ children }: { children: ReactNode }): React.JSX.Element {
  const tokens = useTokens();
  return (
    <Box sx={{ typography: "body2", color: tokens.ink.muted, px: 5, py: 6, textAlign: "center" }}>
      {children}
    </Box>
  );
}

/**
 * Shown while a list is being fetched.
 *
 * Distinct from `Empty` on purpose: rendering "nothing here" during a request states an answer
 * the page does not have yet.
 */
export function Loading({ what }: { what: string }): React.JSX.Element {
  const tokens = useTokens();
  return (
    <Box
      sx={{
        typography: "body2",
        color: tokens.ink.muted,
        px: 5,
        py: 6,
        textAlign: "center",
        display: "flex",
        gap: 2,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <FlexcellSpinner size={18} color={tokens.accent.base} title="" />
      Loading {what}…
    </Box>
  );
}

/** A monospace token — a scope, a client id, an identifier. */
export function Mono({
  children,
  sx,
}: {
  children: ReactNode;
  sx?: SxProps<Theme>;
}): React.JSX.Element {
  const tokens = useTokens();
  return (
    <Box
      component="span"
      sx={merge(
        { fontFamily: tokens.font.mono, fontSize: "0.92em", color: tokens.ink.primary },
        sx,
      )}
    >
      {children}
    </Box>
  );
}

/**
 * An identifier as a chip — a scope, a role key, an audience.
 * Never a status: those are a `StatusBadge`, whose colour carries meaning this does not.
 */
export function Tag({ label, sx }: { label: string; sx?: SxProps<Theme> }): React.JSX.Element {
  const tokens = useTokens();
  return (
    <Box
      component="span"
      sx={merge(
        {
          display: "inline-flex",
          alignItems: "center",
          px: "7px",
          py: "1px",
          fontFamily: tokens.font.mono,
          fontSize: "11px",
          lineHeight: 1.6,
          color: tokens.ink.secondary,
          bgcolor: tokens.surface.cardSunken,
          border: `1px solid ${tokens.line.faint}`,
          borderRadius: `${tokens.radius.sm}px`,
          whiteSpace: "nowrap",
        },
        sx,
      )}
    >
      {label}
    </Box>
  );
}

/** A wrapping run of tags. */
export function Tags({ children }: { children: ReactNode }): React.JSX.Element {
  return <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>{children}</Box>;
}

// ---
// outcomes
// ---

/** An error from the server, shown verbatim. */
export function Failure({ error }: { error: string | null }): React.JSX.Element | null {
  if (error === null) return null;
  return (
    <Alert severity="error" sx={{ mt: 3 }}>
      {error}
    </Alert>
  );
}

/** Explains a missing scope rather than only reporting it. */
export function NotAuthorized({
  scope,
  grantedBy,
}: {
  scope: string;
  grantedBy?: string;
}): React.JSX.Element {
  const tokens = useTokens();
  return (
    <Section title="Not authorized">
      <Box component="p" sx={{ typography: "body1", color: tokens.ink.secondary, m: 0 }}>
        This page needs the <Mono>{scope}</Mono> scope, which your control-plane token does not
        carry.
      </Box>
      <Hint>
        There is no separate administrator sign-in: authority comes from role assignments on your
        subject.
        {grantedBy !== undefined && ` Granted by ${grantedBy}.`}
      </Hint>
    </Section>
  );
}
